<template>
    <div class="tw:w-full">
        <ValidationProvider :name="props.name" :rules="computedRules" v-slot="{ errors }" mode="eager">
            <div class="tw:relative tw:w-full">
                <input
                    v-model="localValue"
                    type="text"
                    :placeholder="' '"
                    :maxlength="maxlengthForType"
                    :disabled="props.disabled"
                    class="tw:block tw:px-2 tw:py-2 tw:w-full tw:text-sm tw:rounded-md tw:border tw:appearance-none tw:focus:outline-none tw:peer"
                    :class="[
                        errors[0]
                            ? 'tw:border-red-500 tw:bg-red-50/50 tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400 tw:focus:bg-white'
                            : 'tw:border-slate-300 tw:bg-white tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400',
                        props.disabled ? 'tw:cursor-not-allowed tw:opacity-60' : ''
                    ]"
                    v-bind="$attrs"
                    @input="handleInput"
                    @change="handleChange"
                />

                <span
                    v-if="props.label"
                    class="tw:absolute tw:uppercase tw:duration-300 tw:z-10 tw:pointer-events-none tw:tracking-wide tw:origin-left tw:bg-white tw:px-1
                    tw:start-1.5 tw:top-[6px] tw:-translate-y-4 tw:scale-75
                    tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:-translate-y-1/2 tw:peer-placeholder-shown:top-1/2 tw:peer-placeholder-shown:font-normal
                    tw:peer-focus:top-2 tw:peer-focus:-translate-y-4 tw:peer-focus:scale-75 tw:peer-focus:px-2 tw:peer-focus:font-bold tw:peer-focus:text-blue-800"
                    :class="[
                        errors[0]
                            ? 'tw:text-red-400 tw:peer-placeholder-shown:bg-transparent tw:peer-focus:bg-white tw:peer-focus:text-blue-800'
                            : 'tw:text-slate-600 tw:font-bold'
                    ]"
                >
                    {{ props.label }}
                </span>
            </div>

            <span
                v-if="errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ formatErrorMessage(errors[0]) }}
            </span>

            <span
                v-if="props.apiValidationError && !errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';

    /**
     * DocumentInput — generic document number input primitive.
     *
     * Accepts an optional `documentType` hint to auto-apply contextual maxlength.
     * The consumer controls validation rules explicitly via `rules` prop —
     * this component does NOT hardcode business rules per document type.
     *
     * Integrates with VeeValidate (ValidationProvider) the same way other `modules/front/ui` inputs do.
     *
     * @prop {String}  value               - Current document value (v-model compatible via @input).
     * @prop {String}  [documentType]      - Hint about the type (e.g. 'DNI', 'NIE', 'PASAPORTE').
     *                                       Used only to compute a sensible maxlength.
     * @prop {String}  [label]             - Floating label text.
     * @prop {String}  [name]              - Field name for VeeValidate.
     * @prop {String|Object} [rules]       - VeeValidate rules.
     * @prop {Boolean} [disabled]          - Disables the input.
     * @prop {String|Array} [apiValidationError] - Server-side validation error to display.
     *
     * @emits input  - New string value on every keystroke.
     * @emits change - New string value on native change event.
     */
    const props = defineProps({
        value: [String, Number],
        documentType: String,
        label: String,
        name: String,
        rules: [String, Object],
        disabled: Boolean,
        apiValidationError: [String, Array],
    });

    const emit = defineEmits(['input', 'change']);

    const computedRules = computed(() => props.rules || '');

    /**
     * Sensible maxlength per document type — purely cosmetic/UX, not a validation rule.
     * Callers wanting strict validation must pass it via `rules`.
     */
    const MAXLENGTH_BY_TYPE = {
        DNI: 9,
        NIF: 9,
        NIE: 9,
        PASAPORTE: 20,
    };
    const DEFAULT_MAXLENGTH = 30;

    const maxlengthForType = computed(() => {
        if (!props.documentType) return DEFAULT_MAXLENGTH;
        const key = props.documentType.toUpperCase();
        return MAXLENGTH_BY_TYPE[key] ?? DEFAULT_MAXLENGTH;
    });

    const localValue = ref(props.value != null ? String(props.value) : '');

    const handleInput = e => {
        const val = e.target.value;
        localValue.value = val;
        emit('input', val);
    };

    const handleChange = e => {
        emit('change', e.target.value);
    };

    const formatErrorMessage = err => {
        const msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    watch(() => props.value, newVal => {
        localValue.value = newVal != null ? String(newVal) : '';
    });
</script>

<script>
    export default {
        inheritAttrs: false
    };
</script>
